<script setup lang="ts">
// PairingDetailsModal — v3.13.57 配对详情 Modal。
//
// 复用于 PairingBanner 的「详情」按钮:展示对端节点的完整识别信息,
// 每行 label + 等宽值 + 一键复制。底部有 [接受] / [拒绝] / [关闭]。
// 不直接调 accept/reject mutation — 通过 emit 通知父组件,父组件
// 统一管理 mutation 与 toast 反馈 (避免双源数据污染)。
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  UserPlus,
  Copy,
  Check,
  Clock,
  Fingerprint,
  KeyRound,
  Server,
  MapPin,
  Hash,
  X,
  CheckCircle2,
} from 'lucide-vue-next'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import Progress from '@/components/ui/Progress.vue'
import type { PairingRequest } from '@/api/cluster'
import { copyToClipboard } from '@/composables/useClipboard'
import { useToast } from '@/composables/useToast'
import { useCountdown } from '@/composables/useCountdown'

const { t, locale } = useI18n()
const toast = useToast()

interface Props {
  modelValue: boolean
  request: PairingRequest | null
  accepting?: boolean
  rejecting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  accepting: false,
  rejecting: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  accept: [request: PairingRequest]
  reject: [request: PairingRequest]
}>()

// 倒计时 (10 分钟 TTL)。PairingBanner 通过 v-for 重新挂载,本组件只关心
// 当前请求的 at。request 为 null 时 safe defaults。
const requestAt = computed<string | undefined>(() => props.request?.at)
const { remainingLabel, remainingRatio, urgent, expired } = useCountdown(requestAt, 10 * 60 * 1000)

const isOpen = computed(() => Boolean(props.modelValue && props.request))

function close(): void {
  emit('update:modelValue', false)
}

// 字段级复制反馈 — 哪个 key 刚复制成功 (用于切换图标)。
const copiedKey = ref<string | null>(null)
let copiedTimer: ReturnType<typeof setTimeout> | null = null

// v3.15.6: 卸载时清定时器 — 弹窗关闭后残留的 setTimeout 仍会写已卸载
// 组件的 ref, 反复开关会逐次累积。
onBeforeUnmount(() => {
  if (copiedTimer !== null) {
    clearTimeout(copiedTimer)
    copiedTimer = null
  }
})

async function copyField(key: string, value: string | undefined): Promise<void> {
  if (!value) return
  const ok = await copyToClipboard(value)
  if (!ok) {
    toast.error(t('common.copyFailed'))
    return
  }
  copiedKey.value = key
  toast.success(t('cluster.pairing.copiedField'))
  if (copiedTimer !== null) clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => {
    copiedKey.value = null
    copiedTimer = null
  }, 1500)
}

// 短指纹 = fingerprint 前 7 字符 (Syncthing 同款视觉锚定)。完整指纹
// 后端存 32 字节 SHA-256 hex,前端直接拿全串展示。
const shortFp = computed<string>(() => (props.request?.fingerprint ?? '').slice(0, 7))

// fingerprint 派生背景色 — 简陋 identicon 替代:HSL 360 取模,文字白色。
const fpColor = computed<string>(() => {
  const fp = props.request?.fingerprint ?? ''
  if (!fp) return 'hsl(220 60% 55%)'
  let h = 0
  for (let i = 0; i < Math.min(fp.length, 16); i++) {
    h = (h * 31 + fp.charCodeAt(i)) & 0xffffffff
  }
  return `hsl(${Math.abs(h) % 360} 60% 55%)`
})

// Addr 拆 IP / 端口 ("1.2.3.4:50052")。可能为 IPv6 "[::1]:50052"。
const addrParts = computed<{ ip: string; port: string }>(() => {
  const a = props.request?.addr ?? ''
  if (!a) return { ip: '—', port: '' }
  // IPv6 形式: "[::1]:50052"
  if (a.startsWith('[')) {
    const end = a.indexOf(']')
    if (end > 0) {
      const ip = a.slice(1, end)
      const rest = a.slice(end + 1)
      const port = rest.startsWith(':') ? rest.slice(1) : ''
      return { ip, port }
    }
  }
  const idx = a.lastIndexOf(':')
  if (idx > 0) return { ip: a.slice(0, idx), port: a.slice(idx + 1) }
  return { ip: a, port: '' }
})

// 节点显示名:真实 name 优先,否则 node_id 短码,再退化到 fingerprint 短码。
const displayName = computed<string>(() => {
  const r = props.request
  if (!r) return ''
  if (r.name) return r.name
  if (r.node_id) return r.node_id
  return shortFp.value || r.fingerprint.slice(0, 12)
})

const subline = computed<string>(() => {
  const r = props.request
  if (!r) return ''
  if (r.name) return r.node_id || shortFp.value
  return t('cluster.pairing.noNameYet')
})

// 完整 fingerprint 作为「证书指纹」展示:全串足够核验,但视觉上仍 code 字体。
const fullFp = computed<string>(() => props.request?.fingerprint ?? '')

// 字段数组 — 复用同一渲染 (label / value / copyKey)。
interface Field {
  key: string
  label: string
  value: string
  copyable: boolean
  mono: boolean
  placeholder?: string
}

const fields = computed<Field[]>(() => {
  const r = props.request
  if (!r) return []
  return [
    { key: 'fp', label: t('cluster.pairing.fieldFingerprint'), value: fullFp.value, copyable: true, mono: true },
    { key: 'short', label: t('cluster.pairing.fieldFingerprintShort'), value: shortFp.value, copyable: true, mono: true, placeholder: '—' },
    { key: 'node', label: t('cluster.pairing.fieldNodeId'), value: r.node_id ?? '', copyable: true, mono: true, placeholder: '—' },
    { key: 'name', label: t('cluster.pairing.fieldName'), value: r.name ?? '', copyable: false, mono: false, placeholder: t('cluster.pairing.fieldNameUnknown') },
    { key: 'addr', label: t('cluster.pairing.fieldAddress'), value: r.addr ?? '', copyable: true, mono: true, placeholder: '—' },
  ]
})

// 格式化请求时间为本地时区字符串。
function formatRequestedAt(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  try {
    return d.toLocaleString(locale.value === 'en' ? 'en-US' : 'zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return iso
  }
}

function accept(): void {
  if (props.request) emit('accept', props.request)
}

function reject(): void {
  if (props.request) emit('reject', props.request)
}
</script>

<template>
  <Modal
    :model-value="isOpen"
    :title="t('cluster.pairing.detailsTitle')"
    size="lg"
    @update:model-value="(v) => emit('update:modelValue', v)"
  >
    <div v-if="request" class="space-y-4">
      <!-- 头部:identicon + name + 副信息 -->
      <div class="flex items-start gap-3">
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base font-bold text-white shadow-sm"
          :style="{ backgroundColor: fpColor }"
          aria-hidden="true"
        >
          <UserPlus :size="20" />
        </div>
        <div class="min-w-0 flex-1">
          <h4 class="truncate text-base font-semibold text-fg">{{ displayName }}</h4>
          <p class="mt-0.5 flex items-center gap-1.5 truncate text-xs text-fg-muted">
            <KeyRound :size="12" class="shrink-0" />
            <span class="truncate font-mono">{{ subline }}</span>
          </p>
        </div>
      </div>

      <!-- 倒计时卡片:独立显眼,不足 1 分钟转红 -->
      <div
        class="flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors"
        :class="urgent || expired ? 'border-danger/40 bg-danger/10' : 'border-warning/40 bg-warning/10'"
        role="status"
      >
        <Clock :size="16" :class="urgent || expired ? 'text-danger animate-pulse' : 'text-warning'" />
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-3">
            <span class="text-xs font-medium" :class="urgent || expired ? 'text-danger' : 'text-warning'">
              {{ t('cluster.pairing.fieldTtl') }}
            </span>
            <span
              class="font-mono text-sm font-semibold tabular-nums"
              :class="urgent || expired ? 'text-danger' : 'text-fg'"
            >
              {{ remainingLabel }}
            </span>
          </div>
          <Progress
            class="mt-1.5"
            :value="Math.round(remainingRatio * 100)"
            :max="100"
            size="sm"
            :variant="urgent || expired ? 'danger' : 'warning'"
          />
        </div>
      </div>

      <!-- 字段表 -->
      <div class="overflow-hidden rounded-lg border border-border bg-bg-elevated">
        <dl class="divide-y divide-border">
          <div
            v-for="(f, idx) in fields"
            :key="f.key"
            class="flex items-center gap-3 px-3 py-2"
            :class="idx === 0 ? '' : ''"
          >
            <dt class="flex w-28 shrink-0 items-center gap-1.5 text-xs font-medium text-fg-muted">
              <Fingerprint v-if="f.key === 'fp'" :size="12" class="shrink-0" />
              <Hash v-else-if="f.key === 'short'" :size="12" class="shrink-0" />
              <Server v-else-if="f.key === 'node'" :size="12" class="shrink-0" />
              <UserPlus v-else-if="f.key === 'name'" :size="12" class="shrink-0" />
              <MapPin v-else-if="f.key === 'addr'" :size="12" class="shrink-0" />
              <span class="truncate">{{ f.label }}</span>
            </dt>
            <dd
              class="min-w-0 flex-1 truncate text-xs"
              :class="f.mono ? 'font-mono text-fg' : 'text-fg'"
            >
              <span v-if="f.value">{{ f.value }}</span>
              <span v-else class="italic text-fg-subtle">{{ f.placeholder || '—' }}</span>
            </dd>
            <Tooltip v-if="f.copyable && f.value" :content="copiedKey === f.key ? t('cluster.pairing.copiedField') : t('cluster.pairing.copyField')">
              <Button
                variant="ghost"
                size="sm"
                icon-only
                :aria-label="t('cluster.pairing.copyField')"
                @click="copyField(f.key, f.value)"
              >
                <Check v-if="copiedKey === f.key" :size="13" class="text-success" />
                <Copy v-else :size="13" />
              </Button>
            </Tooltip>
            <span v-else class="w-8 shrink-0" />
          </div>

          <!-- 请求时间 (本地化格式,无法复制 — 但显示) -->
          <div class="flex items-center gap-3 px-3 py-2">
            <dt class="flex w-28 shrink-0 items-center gap-1.5 text-xs font-medium text-fg-muted">
              <Clock :size="12" class="shrink-0" />
              <span class="truncate">{{ t('cluster.pairing.fieldRequestedAt') }}</span>
            </dt>
            <dd class="min-w-0 flex-1 truncate text-xs text-fg">
              {{ formatRequestedAt(request.at) }}
            </dd>
            <Tooltip :content="t('cluster.pairing.copyField')">
              <Button
                variant="ghost"
                size="sm"
                icon-only
                :aria-label="t('cluster.pairing.copyField')"
                @click="copyField('time', request.at)"
              >
                <Check v-if="copiedKey === 'time'" :size="13" class="text-success" />
                <Copy v-else :size="13" />
              </Button>
            </Tooltip>
          </div>
        </dl>
      </div>
    </div>

    <template #footer>
      <Button variant="ghost" size="sm" @click="close">
        {{ t('common.close') }}
      </Button>
      <Button
        variant="outline"
        size="sm"
        :loading="rejecting"
        :disabled="accepting"
        @click="reject"
      >
        <template #icon><X :size="14" /></template>
        {{ t('cluster.pairing.reject') }}
      </Button>
      <Button
        variant="primary"
        size="sm"
        :loading="accepting"
        :disabled="rejecting"
        @click="accept"
      >
        <template #icon><CheckCircle2 :size="14" /></template>
        {{ t('cluster.pairing.accept') }}
      </Button>
    </template>
  </Modal>
</template>
